import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    addressHiddenTooltip,
    importBlockedTooltip,
    isAddressHidden,
    isImportBlocked,
} from "../../utils/mlsCandidateImport";
import { duplicateHint } from "../../utils/mlsCandidateGrouping";
import { toast } from "react-toastify";
import {
    Home,
    Inbox,
    Info,
    Loader2,
    MapPin,
    RefreshCw,
    RotateCcw,
    Trash2,
    X,
} from "lucide-react";
import axios from "axios";
// Side-effect import so the axios singleton carries the Gandalf/impersonation/401
// interceptors regardless of import ordering (same rationale as mlsCandidatesApi).
import "../../utils/axiosConfig";
import CustomToast from "../misc/CustomToast";
import { Button } from "../misc/Button";
import Checkbox from "../misc/Checkbox";
import SimpleTooltip from "../misc/SimpleTooltip";
import ReconfirmOverlay from "../misc/ReconfirmOverlay";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "../misc/Dialog";
import { Tabs, TabsList, TabsTrigger } from "../misc/Tabs";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from "../misc/Pagination";
import {
    addMlsCandidates,
    discardMlsCandidates,
    getMlsCandidates,
    refreshMlsCandidates,
    restoreMlsCandidates,
} from "./mlsCandidatesApi";
import {
    MlsCandidateSnapshot,
    MlsListingCandidate,
} from "../../types/mlsCandidate";
import { AllListings } from "../../types/allListings";
import { getAuthHeader } from "../../utils/authHeader";
import { cn } from "../../utils/classMerger";

interface MlsListingReviewModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // Fired after any add/discard/restore/delete so the parent can refetch listings + the count.
    onChanged?: () => void;
    // Which tab to show when the modal opens. Defaults to "MyListings" so opening the
    // dialog lands on the user's current listings first.
    initialTab?: ReviewTab;
    // The user's current listings (the same set shown on the My Listings page). Powers the
    // "My Listings" tab so the user can delete an imported listing without leaving the modal.
    listings?: AllListings[];
}

type ReviewTab = "Pending" | "Discarded" | "MyListings";

const PAGE_SIZE = 4;

// An address-hidden row has no street to show; say why.
const primaryLine = (snapshot: MlsCandidateSnapshot): string =>
    snapshot.UnparsedAddress?.trim() ||
    (snapshot.addressUnavailable ? "Address hidden by MLS" : "Address unavailable");

const secondaryLine = (snapshot: MlsCandidateSnapshot): string => {
    const cityState = [snapshot.PostalCity, snapshot.StateOrProvince]
        .filter(Boolean)
        .join(", ");
    return [cityState, snapshot.PostalCode].filter(Boolean).join(" ");
};

// Trestle PropertyType tokens are space-less (e.g. "ResidentialLease"). Split on capitals for
// a readable label — same formatting the listing detail page (ListingHeader) uses.
const formatPropertyType = (propertyType: string): string =>
    propertyType
        .replace(/([A-Z])/g, " $1")
        .replace(/,/g, ", ")
        .trim();

const metaLine = (candidate: MlsListingCandidate): string => {
    const parts: string[] = [];
    if (candidate.ListingId) parts.push(`MLS# ${candidate.ListingId}`);
    if (candidate.listingData?.PropertyType) {
        parts.push(formatPropertyType(String(candidate.listingData.PropertyType)));
    }
    const photos = candidate.listingData?.PhotosCount;
    if (typeof photos === "number" && photos > 0) {
        parts.push(`${photos} photo${photos === 1 ? "" : "s"}`);
    }
    return parts.join("  ·  ");
};

// The "My Listings" tab renders already-imported listings with the same three lines the
// review tabs use: street (primary), city/state/ZIP (secondary), and "MLS# · type · photos"
// (meta). formattedAddress is a single "street, city, state zip" string, so split it at the
// first comma into the primary/secondary lines.
const listingPrimary = (listing: AllListings): string => {
    const addr = listing.formattedAddress?.trim();
    if (!addr) return "Address unavailable";
    const idx = addr.indexOf(",");
    return idx > 0 ? addr.slice(0, idx).trim() : addr;
};

const listingSecondary = (listing: AllListings): string => {
    const addr = listing.formattedAddress?.trim() || "";
    const idx = addr.indexOf(",");
    return idx > 0 ? addr.slice(idx + 1).trim() : "";
};

const listingMetaLine = (listing: AllListings): string => {
    const parts: string[] = [];
    if (listing.mlsId) parts.push(`MLS# ${listing.mlsId}`);
    if (listing.mlsPropertyType) {
        parts.push(formatPropertyType(listing.mlsPropertyType));
    }
    if (typeof listing.imageCount === "number" && listing.imageCount > 0) {
        parts.push(
            `${listing.imageCount} photo${listing.imageCount === 1 ? "" : "s"}`,
        );
    }
    return parts.join("  ·  ");
};

const MlsListingReviewModal: React.FC<MlsListingReviewModalProps> = ({
    open,
    onOpenChange,
    onChanged,
    initialTab = "MyListings",
    listings = [],
}) => {
    const navigate = useNavigate();
    const [tab, setTab] = useState<ReviewTab>(initialTab);
    // Read the latest initialTab when the modal opens without making it an effect dep — we
    // only want to set the tab on the open transition, never yank it mid-session if the
    // parent's counts change while the modal is open.
    const initialTabRef = useRef(initialTab);
    initialTabRef.current = initialTab;
    const [pending, setPending] = useState<MlsListingCandidate[]>([]);
    const [discarded, setDiscarded] = useState<MlsListingCandidate[]>([]);
    const [available, setAvailable] = useState<number>(0);
    const [activeUsed, setActiveUsed] = useState<number>(0);
    const [activeLimit, setActiveLimit] = useState<number>(0);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    // Listing targeted by the "My Listings" tab delete button (drives the shared confirm),
    // plus a flag for the in-flight delete request.
    const [deleteTarget, setDeleteTarget] = useState<AllListings | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [pendingRes, discardedRes] = await Promise.all([
                getMlsCandidates("Pending"),
                getMlsCandidates("Discarded"),
            ]);
            setPending(pendingRes.candidates);
            setAvailable(pendingRes.available);
            setActiveUsed(pendingRes.activeUsed);
            setActiveLimit(pendingRes.activeLimit);
            setDiscarded(discardedRes.candidates);
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to load listings to review. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            // Clear any in-progress delete state on close so a dialog dismissed (X / Escape /
            // outside) while the delete confirm was open doesn't re-show that irreversible
            // confirm for a stale listing the next time it opens.
            setDeleteTarget(null);
            setDeleting(false);
            return;
        }
        setSelected(new Set());
        setPage(1);
        setTab(initialTabRef.current);
        load();
    }, [open, load]);

    // Reset paging when switching tabs so we never land on an out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [tab]);

    const atCap = available <= 0;
    const overCap = selected.size > available;

    // Selection is uncapped so the user can always archive listings they don't want
    // (archiving never consumes a slot). The active-listing cap is enforced on the Add
    // action below and re-enforced by the server.
    const toggle = useCallback((listingKey: string, isSelected: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (isSelected) {
                next.add(listingKey);
            } else {
                next.delete(listingKey);
            }
            return next;
        });
    }, []);

    const handleContactSupport = useCallback(() => {
        onOpenChange(false);
        navigate("/help", { state: { tab: "contact" } });
    }, [navigate, onOpenChange]);

    // Re-run a per-user, review-only MLS discovery (never auto-adds) and reload the lists.
    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await refreshMlsCandidates();
            await load();
            onChanged?.();
        } catch {
            toast.error(
                <CustomToast
                    message="Couldn't refresh MLS listings. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setRefreshing(false);
        }
    }, [load, onChanged]);

    const candidateList = tab === "Discarded" ? discarded : pending;

    // MB-1430: the Pending tab lists blocked candidates too (flagged, so the
    // agent knows they exist), but the label promises imports - so it counts
    // only the ones that can actually be imported.
    const importableCount = useMemo(
        () => pending.filter((c) => !isImportBlocked(c.listingData)).length,
        [pending],
    );
    const currentCount =
        tab === "MyListings" ? listings.length : candidateList.length;
    const totalPages = Math.max(1, Math.ceil(currentCount / PAGE_SIZE));
    const candidatePageItems = useMemo(
        () => candidateList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [candidateList, page],
    );
    const listingPageItems = useMemo(
        () => listings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [listings, page],
    );

    // Keep the page in range if the active list shrinks under us (e.g. after a delete or
    // an optimistic add/discard drops the last item on the current page).
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const handleAdd = async () => {
        if (selected.size === 0) return;
        setSubmitting(true);
        try {
            const result = await addMlsCandidates(
                Array.from(selected),
            );
            const addedCount = result.added.length;
            if (addedCount > 0) {
                toast.success(
                    <CustomToast
                        message={`${addedCount} listing${
                            addedCount === 1 ? "" : "s"
                        } added — they're processing now.`}
                        type="success"
                    />,
                    { autoClose: 3000 },
                );
            }
            // MB-1611: the MLS data feed is down, so the add is remembered and runs
            // on its own once the feed is back. Nothing to click again.
            const queuedCount = result.queued?.length ?? 0;
            if (queuedCount > 0) {
                toast.info(
                    <CustomToast
                        message={`${queuedCount} listing${
                            queuedCount === 1 ? "" : "s"
                        } queued — the MLS feed is temporarily unavailable; ${
                            queuedCount === 1 ? "it" : "they"
                        } will be added automatically when it's back.`}
                        type="success"
                    />,
                    { autoClose: 6000 },
                );
            }
            if (result.rejected.length > 0 || result.failed.length > 0) {
                const blocked = result.rejected.length + result.failed.length;
                toast.error(
                    <CustomToast
                        message={`${blocked} listing${
                            blocked === 1 ? "" : "s"
                        } couldn't be added (active-listing limit reached).`}
                        type="error"
                    />,
                    { autoClose: 4000 },
                );
            }
            setSelected(new Set());
            onChanged?.();
            // Close the review dialog once the add request has returned.
            onOpenChange(false);
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to add the selected listings. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleDiscard = async () => {
        if (selected.size === 0) return;
        // Optimistically move the selected candidates Pending → Excluded — no toast, no reload.
        const keySet = new Set(selected);
        const moving = pending.filter((c) => keySet.has(c.ListingKey));
        setPending((prev) => prev.filter((c) => !keySet.has(c.ListingKey)));
        setDiscarded((prev) => [...moving, ...prev]);
        setSelected(new Set());
        try {
            await discardMlsCandidates(Array.from(keySet));
            // Keep the parent banner count in sync (does not reload this modal's lists).
            onChanged?.();
        } catch {
            setDiscarded((prev) =>
                prev.filter((c) => !keySet.has(c.ListingKey)),
            );
            setPending((prev) => [...moving, ...prev]);
            toast.error(
                <CustomToast
                    message="Failed to exclude the selected listings. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        }
    };

    const handleRestore = async (listingKey: string) => {
        // Optimistic move Excluded → Pending: drop it from the Excluded list and prepend it to
        // Pending immediately — no toast, no full reload. Revert if the request fails.
        const candidate = discarded.find((c) => c.ListingKey === listingKey);
        if (!candidate) return;
        setDiscarded((prev) => prev.filter((c) => c.ListingKey !== listingKey));
        setPending((prev) => [candidate, ...prev]);
        try {
            await restoreMlsCandidates([listingKey]);
            // The api stamps duplicateOf on restore (MB-1520); re-read Pending
            // quietly so the "Same property as" hint shows without a Refresh.
            const pendingRes = await getMlsCandidates("Pending");
            setPending(pendingRes.candidates);
            // Keep the parent banner count in sync (does not reload this modal's lists).
            onChanged?.();
        } catch {
            setPending((prev) =>
                prev.filter((c) => c.ListingKey !== listingKey),
            );
            setDiscarded((prev) => [candidate, ...prev]);
            toast.error(
                <CustomToast
                    message="Failed to restore the listing. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        }
    };

    // Delete an imported listing straight from the "My Listings" tab — mirrors the listing
    // card's delete (POST /listing/delete + the same confirm copy and gating). Close the
    // confirm immediately on click, then show the loader while the request runs; a successful
    // delete triggers onChanged so the parent refetches and the updated `listings` flow back.
    const handleDeleteListing = async () => {
        const target = deleteTarget;
        if (!target) return;
        setDeleteTarget(null);
        setDeleting(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(
                `${backendUrl}/listing/delete`,
                { listingId: target.id, listingType: target.listingType },
                { headers },
            );
            if (response.status === 200) {
                toast.success(
                    <CustomToast
                        message="Listing deleted successfully."
                        type="success"
                    />,
                    { autoClose: 3000 },
                );
                onChanged?.();
            } else {
                throw new Error("Failed to delete listing.");
            }
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to delete listing. Please try again."
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setDeleting(false);
        }
    };

    const renderRow = (candidate: MlsListingCandidate) => {
        const snapshot = candidate.listingData ?? {};
        const isArchived = tab === "Discarded";
        const isSelected = selected.has(candidate.ListingKey);
        // MB-1430: the MLS forbids this listing on the internet - visible here
        // so the agent knows it exists, but never importable.
        const importBlocked = isImportBlocked(snapshot);
        const blockedTooltip = importBlockedTooltip();
        // The MLS hides the address: importable, marketed as "City, ST ZIP";
        // the chip says so.
        const addressHidden = isAddressHidden(snapshot);
        // MB-1611: Add was clicked while the MLS feed was down; it runs on its own later.
        const queued = Boolean(candidate.queuedAddAt);
        const dupHint = duplicateHint(candidate);

        return (
            <div
                key={candidate.ListingKey}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-secondary hover:bg-gray-50 min-[480px]:flex-row min-[480px]:items-center"
            >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    {!isArchived && (
                        <SimpleTooltip
                            message="This listing can't be imported"
                            visible={importBlocked}
                            className="shrink-0"
                        >
                            <span
                                className={
                                    importBlocked
                                        ? "pointer-events-none"
                                        : undefined
                                }
                            >
                                <Checkbox
                                    variant="radix"
                                    disabled={importBlocked}
                                    checked={isSelected && !importBlocked}
                                    onChange={(value) => {
                                        if (!importBlocked) {
                                            toggle(candidate.ListingKey, value);
                                        }
                                    }}
                                    id={`cand-${candidate.ListingKey}`}
                                />
                            </span>
                        </SimpleTooltip>
                    )}

                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2 text-primary">
                            <Home className="h-4 w-4 shrink-0 text-[#e9c468]" />
                            <span className="truncate text-sm font-semibold">
                                {primaryLine(snapshot)}
                            </span>
                        </div>
                        {secondaryLine(snapshot) && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                    {secondaryLine(snapshot)}
                                </span>
                            </div>
                        )}
                        {(metaLine(candidate) || importBlocked || addressHidden || queued) && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-gray-400">
                                {metaLine(candidate) && (
                                    <span className="truncate">
                                        {metaLine(candidate)}
                                    </span>
                                )}
                                {queued && (
                                    <span
                                        data-testid="candidate-queued"
                                        className="shrink-0 rounded-full bg-[#E9C468] px-2 py-0.5 text-[11px] font-semibold text-[#193456]"
                                    >
                                        Queued — waiting for the MLS feed
                                    </span>
                                )}
                                {importBlocked && (
                                    <SimpleTooltip
                                        message={blockedTooltip}
                                        visible
                                        className="shrink-0"
                                        tooltipClassName="bg-secondary text-primary"
                                    >
                                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#e9c468] px-2 py-0.5 text-[11px] font-medium text-[#193456]">
                                            <Info className="h-3 w-3" />
                                            Can't be imported
                                        </span>
                                    </SimpleTooltip>
                                )}
                                {addressHidden && !importBlocked && (
                                    <SimpleTooltip
                                        message={addressHiddenTooltip(snapshot)}
                                        visible
                                        className="shrink-0"
                                        tooltipClassName="bg-secondary text-primary"
                                    >
                                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                                            <Info className="h-3 w-3" />
                                            Address hidden
                                        </span>
                                    </SimpleTooltip>
                                )}
                            </div>
                        )}
                        {dupHint && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-gray-500">
                                <Info className="h-3 w-3 shrink-0" aria-hidden />
                                <span>{dupHint}</span>
                            </p>
                        )}
                    </div>
                </div>

                {isArchived && (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={submitting}
                        onClick={() => handleRestore(candidate.ListingKey)}
                        className="w-full shrink-0 min-[480px]:w-auto"
                    >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Re-add
                    </Button>
                )}
            </div>
        );
    };

    const renderListingRow = (listing: AllListings) => {
        // Same delete gate as the listing card / detail page: a listing that is still
        // processing or MLS-syncing can't be deleted, and a generating campaign blocks
        // delete (shown as a disabled button with a tooltip).
        const canDelete = listing.status !== "Processing" && !listing.isSyncing;
        const deleteBlockedByCampaign = listing.campaignStatus === "PROCESSING";
        const secondary = listingSecondary(listing);
        const meta = listingMetaLine(listing);
        const statusLabel = listing.status
            ?.replace(/([A-Z])/g, " $1")
            .trim();

        return (
            <div
                key={listing.id}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-secondary hover:bg-gray-50 min-[480px]:flex-row min-[480px]:items-center"
            >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2 text-primary">
                            <Home className="h-4 w-4 shrink-0 text-[#e9c468]" />
                            <span className="truncate text-sm font-semibold">
                                {listingPrimary(listing)}
                            </span>
                        </div>
                        {secondary && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">{secondary}</span>
                            </div>
                        )}
                        {(meta || statusLabel) && (
                            <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-gray-400">
                                {meta && <span className="truncate">{meta}</span>}
                                {statusLabel && (
                                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                                        {statusLabel}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {canDelete && (
                    <SimpleTooltip
                        message="Please wait for the campaign to finish before deleting the listing"
                        visible={deleteBlockedByCampaign}
                        className="w-full shrink-0 min-[480px]:w-auto"
                    >
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={deleting || deleteBlockedByCampaign}
                            onClick={() => {
                                if (!deleteBlockedByCampaign) {
                                    setDeleteTarget(listing);
                                }
                            }}
                            className="w-full min-[480px]:w-auto"
                        >
                            <Trash2 className="mr-1 h-4 w-4" />
                            Delete
                        </Button>
                    </SimpleTooltip>
                )}
            </div>
        );
    };

    const emptyState = (label: string) => (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-gray-400">
            <Inbox className="h-10 w-10" />
            <p className="text-sm">{label}</p>
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="relative flex h-[85dvh] max-h-[calc(94dvh-2rem)] w-full max-w-2xl flex-col overflow-y-hidden bg-white p-0 min-[640px]:h-[46rem]"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => {
                    // Escape closes the delete confirm, not the whole dialog behind it.
                    if (deleteTarget) {
                        e.preventDefault();
                        setDeleteTarget(null);
                    }
                }}
            >
                <div className="shrink-0 border-b border-gray-100 p-4 pb-3 min-[640px]:p-6 min-[640px]:pb-4">
                    <div className="flex items-start justify-between gap-4">
                        <DialogTitle>Active MLS Listings Available</DialogTitle>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => onOpenChange(false)}
                            className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        Import active MLS listings, exclude the ones you don&apos;t
                        want, or manage the listings already in Marabot.
                    </p>

                    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                        {activeLimit > 0 ? (
                            <p className="text-sm text-gray-500">
                                <span className="font-semibold text-primary">
                                    {available} slot
                                    {available === 1 ? "" : "s"}
                                </span>{" "}
                                available &middot; {activeUsed} of {activeLimit}{" "}
                                used
                            </p>
                        ) : (
                            <span />
                        )}
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={handleRefresh}
                                disabled={refreshing || submitting}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-secondary disabled:opacity-60"
                            >
                                <RefreshCw
                                    className={cn(
                                        "h-4 w-4",
                                        refreshing && "animate-spin",
                                    )}
                                />
                                Refresh
                            </button>
                            <Link
                                to="/settings/mls-details"
                                onClick={() => onOpenChange(false)}
                                className="text-sm text-primary underline underline-offset-2 hover:text-secondary"
                            >
                                MLS Agent Profile
                            </Link>
                        </div>
                    </div>

                    <Tabs
                        value={tab}
                        onValueChange={(value) => setTab(value as ReviewTab)}
                        className="mt-3"
                    >
                        <TabsList className="w-full flex-nowrap gap-1 p-1.5 min-[640px]:gap-2 min-[640px]:p-2">
                            <TabsTrigger
                                value="MyListings"
                                className="min-w-0 flex-1 px-2 text-xs min-[640px]:px-3 min-[640px]:text-sm"
                            >
                                My Listings ({listings.length})
                            </TabsTrigger>
                            <TabsTrigger
                                value="Pending"
                                className="min-w-0 flex-1 px-2 text-xs min-[640px]:px-3 min-[640px]:text-sm"
                            >
                                <span className="min-[640px]:hidden">
                                    To Import ({importableCount})
                                </span>
                                <span className="hidden min-[640px]:inline">
                                    Available to Import ({importableCount})
                                </span>
                            </TabsTrigger>
                            <TabsTrigger
                                value="Discarded"
                                className="min-w-0 flex-1 px-2 text-xs min-[640px]:px-3 min-[640px]:text-sm"
                            >
                                Excluded ({discarded.length})
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 min-[640px]:px-6 min-[640px]:py-4">
                    {tab === "Discarded" && (
                        <div className="mb-3 rounded-md bg-gray-100 p-3">
                            <p className="text-sm text-gray-600">
                                <strong className="text-primary">Tip:</strong> These
                                are listings you&apos;ve excluded — they won&apos;t be
                                shown or posted again unless you re-add them.
                            </p>
                        </div>
                    )}
                    {tab === "MyListings" && (
                        <div className="mb-3 rounded-md bg-gray-100 p-3">
                            <p className="text-sm text-gray-600">
                                <strong className="text-primary">Tip:</strong> These
                                are the listings currently active in Marabot. Deleting
                                a listing permanently removes it — this can&apos;t be
                                undone.
                            </p>
                        </div>
                    )}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-primary">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : tab === "MyListings" ? (
                        <div className="space-y-2">
                            {listings.length === 0
                                ? emptyState("You don't have any listings yet.")
                                : listingPageItems.map(renderListingRow)}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {candidateList.length === 0
                                ? emptyState(
                                      tab === "Pending"
                                          ? "No listings waiting for review."
                                          : "Nothing excluded.",
                                  )
                                : candidatePageItems.map(renderRow)}
                        </div>
                    )}

                    {!loading && totalPages > 1 && (
                        <Pagination className="mt-4">
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPage((p) => Math.max(1, p - 1));
                                        }}
                                    />
                                </PaginationItem>
                                <PaginationItem>
                                    <span className="px-3 text-sm text-primary">
                                        Page {page} of {totalPages}
                                    </span>
                                </PaginationItem>
                                <PaginationItem>
                                    <PaginationNext
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPage((p) => Math.min(totalPages, p + 1));
                                        }}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    )}
                </div>

                {tab === "Pending" && (
                    <div className="shrink-0 border-t border-gray-100 p-4">
                        <div className="mb-2 text-center text-xs text-gray-500">
                            {atCap ? (
                                <span className="text-[#d23600]">
                                    You&apos;ve reached the active listing limit for
                                    your plan. To add more, please upgrade your plan by{" "}
                                    <button
                                        type="button"
                                        onClick={handleContactSupport}
                                        className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
                                    >
                                        contacting support
                                    </button>
                                    , or exclude the ones you don&apos;t want.
                                </span>
                            ) : overCap ? (
                                <span className="text-[#d23600]">
                                    You can add up to {available} listing
                                    {available === 1 ? "" : "s"} on your current plan —
                                    unselect {selected.size - available} to continue, or{" "}
                                    <button
                                        type="button"
                                        onClick={handleContactSupport}
                                        className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
                                    >
                                        contact support
                                    </button>{" "}
                                    to upgrade.
                                </span>
                            ) : (
                                <span>
                                    {selected.size} of {available} slot
                                    {available === 1 ? "" : "s"} selected
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 min-[480px]:flex-row min-[480px]:justify-end">
                            <Button
                                variant="outline"
                                disabled={submitting || selected.size === 0}
                                onClick={handleDiscard}
                            >
                                Exclude Selected
                            </Button>
                            <Button
                                disabled={
                                    submitting ||
                                    selected.size === 0 ||
                                    atCap ||
                                    overCap
                                }
                                onClick={handleAdd}
                            >
                                {submitting && (
                                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                )}
                                Import Selected ({selected.size}
                                {available > 0 ? `/${available}` : ""})
                            </Button>
                        </div>
                    </div>
                )}

                {deleting && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/70">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                )}

                <ReconfirmOverlay
                    isOpen={!!deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDeleteListing}
                    title="Delete Listing"
                    message="Are you sure you want to delete this listing?"
                    messageTwo="This action cannot be undone."
                    confirmText="Delete Listing"
                    cancelText="Cancel"
                    reverse={true}
                />
            </DialogContent>
        </Dialog>
    );
};

export default MlsListingReviewModal;
